import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
 id: string;
 message: string;
 type: ToastType;
}

export interface DialogOptions {
 title: string;
 message: string;
 type?: 'confirm' | 'success' | 'error' | 'info';
 confirmText?: string;
 cancelText?: string;
}

interface NotificationContextType {
 toast: {
 success: (msg: string) => void;
 error: (msg: string) => void;
 warning: (msg: string) => void;
 info: (msg: string) => void;
 };
 confirm: (title: string, message: string, confirmText?: string, cancelText?: string) => Promise<boolean>;
 showDialog: (options: DialogOptions) => Promise<boolean>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function useNotification() {
 const context = useContext(NotificationContext);
 if (!context) {
 throw new Error('useNotification must be used within a NotificationProvider');
 }
 return context;
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
 const [toasts, setToasts] = useState<Toast[]>([]);
 const [dialog, setDialog] = useState<{
 options: DialogOptions;
 resolve: (value: boolean) => void;
 } | null>(null);

 const removeToast = useCallback((id: string) => {
 setToasts((prev) => prev.filter((t) => t.id !== id));
 }, []);

 const addToast = useCallback((message: string, type: ToastType) => {
 const id = Math.random().toString(36).substring(2, 9);
 setToasts((prev) => [...prev, { id, message, type }]);
 setTimeout(() => {
 removeToast(id);
 }, 4000);
 }, [removeToast]);

 const toast = {
 success: useCallback((msg: string) => addToast(msg, 'success'), [addToast]),
 error: useCallback((msg: string) => addToast(msg, 'error'), [addToast]),
 warning: useCallback((msg: string) => addToast(msg, 'warning'), [addToast]),
 info: useCallback((msg: string) => addToast(msg, 'info'), [addToast]),
 };

 const showDialog = useCallback((options: DialogOptions) => {
 return new Promise<boolean>((resolve) => {
 setDialog({ options, resolve });
 });
 }, []);

 const confirm = useCallback((title: string, message: string, confirmText = 'Yes, Confirm', cancelText = 'Cancel') => {
 return showDialog({ title, message, type: 'confirm', confirmText, cancelText });
 }, [showDialog]);

 // Optionally route standard window alerts to Toast to catch unrefactored legacy code
 useEffect(() => {
 window.alert = (message: string) => {
 toast.info(message);
 };
 }, [toast]);

 return (
 <NotificationContext.Provider value={{ toast, confirm, showDialog }}>
 {children}

 {/* Floating Toast Notification Drawer Container */}
 <div
 style={{
 position: 'fixed',
 top: '24px',
 right: '24px',
 zIndex: 99999,
 display: 'flex',
 flexDirection: 'column',
 gap: '12px',
 pointerEvents: 'none',
 maxWidth: '380px',
 width: '100%',
 }}
 >
 {toasts.map((t) => {
 const typeColors = {
 success: { bg: 'rgba(16, 185, 129, 0.12)', border: '1px solid rgba(16, 185, 129, 0.3)', iconColor: '#10b981', symbol: '✓' },
 error: { bg: 'rgba(239, 68, 68, 0.12)', border: '1px solid rgba(239, 68, 68, 0.3)', iconColor: '#ef4444', symbol: '✕' },
 warning: { bg: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.3)', iconColor: '#f59e0b', symbol: '⚠️' },
 info: { bg: 'rgba(59, 130, 246, 0.12)', border: '1px solid rgba(59, 130, 246, 0.3)', iconColor: '#3b82f6', symbol: 'ℹ' },
 };
 const colors = typeColors[t.type] || typeColors.info;

 return (
 <div
 key={t.id}
 className="animate-toast-in"
 style={{
 pointerEvents: 'auto',
 backgroundColor: 'var(--bg-surface, #1f2937)',
 backgroundImage: `linear-gradient(to right, ${colors.bg}, transparent)`,
 border: colors.border,
 borderRadius: '12px',
 padding: '14px 16px',
 boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.3)',
 backdropFilter: 'blur(8px)',
 display: 'flex',
 alignItems: 'center',
 gap: '12px',
 position: 'relative',
 overflow: 'hidden',
 }}
 >
 {/* Left Indicator Pillar */}
 <div
 style={{
 width: '32px',
 height: '32px',
 borderRadius: '50%',
 backgroundColor: colors.bg,
 border: colors.border,
 display: 'flex',
 alignItems: 'center',
 justifyContent: 'center',
 fontWeight: 700,
 fontSize: '14px',
 color: colors.iconColor,
 flexShrink: 0,
 }}
 >
 {colors.symbol}
 </div>

 {/* Message Block */}
 <div style={{ flex: 1, fontSize: '13px', fontWeight: 500, color: 'var(--text-primary, #f3f4f6)', lineHeight: 1.4 }}>
 {t.message}
 </div>

 {/* Close Button */}
 <button
 onClick={() => removeToast(t.id)}
 style={{
 background: 'none',
 border: 'none',
 color: 'var(--text-muted, #9ca3af)',
 cursor: 'pointer',
 fontSize: '16px',
 padding: '4px',
 lineHeight: 1,
 display: 'flex',
 alignItems: 'center',
 justifyContent: 'center',
 opacity: 0.7,
 transition: 'opacity 0.2s',
 }}
 onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
 onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
 >
 &times;
 </button>

 {/* Animated Progress Timer Line */}
 <div
 style={{
 position: 'absolute',
 bottom: 0,
 left: 0,
 height: '3px',
 backgroundColor: colors.iconColor,
 width: '100%',
 animation: 'shimmer 4s linear forwards',
 opacity: 0.4,
 }}
 />
 </div>
 );
 })}
 </div>

 {/* Global Dialog Modal Overlay */}
 {dialog && (
 <div
 className="animate-modal-fade"
 style={{
 position: 'fixed',
 top: 0,
 left: 0,
 right: 0,
 bottom: 0,
 backgroundColor: 'rgba(0, 0, 0, 0.6)',
 backdropFilter: 'blur(4px)',
 display: 'flex',
 alignItems: 'center',
 justifyContent: 'center',
 zIndex: 999999,
 padding: '20px',
 }}
 >
 <div
 className="animate-modal-scale"
 style={{
 backgroundColor: 'var(--bg-surface, #1f2937)',
 border: '1px solid var(--color-border, #374151)',
 borderRadius: '16px',
 padding: '24px',
 maxWidth: '440px',
 width: '100%',
 boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
 display: 'flex',
 flexDirection: 'column',
 gap: '16px',
 }}
 >
 {/* Header Title with Optional Symbol Icon */}
 <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
 <div
 style={{
 width: '36px',
 height: '36px',
 borderRadius: '50%',
 backgroundColor:
 dialog.options.type === 'confirm'
 ? 'rgba(245, 158, 11, 0.1)'
 : dialog.options.type === 'error'
 ? 'rgba(239, 68, 68, 0.1)'
 : 'rgba(16, 185, 129, 0.1)',
 border:
 dialog.options.type === 'confirm'
 ? '1px solid rgba(245, 158, 11, 0.2)'
 : dialog.options.type === 'error'
 ? '1px solid rgba(239, 68, 68, 0.2)'
 : '1px solid rgba(16, 185, 129, 0.2)',
 display: 'flex',
 alignItems: 'center',
 justifyContent: 'center',
 fontSize: '16px',
 color:
 dialog.options.type === 'confirm'
 ? '#f59e0b'
 : dialog.options.type === 'error'
 ? '#ef4444'
 : '#10b981',
 flexShrink: 0,
 }}
 >
 {dialog.options.type === 'confirm'
 ? '?'
 : dialog.options.type === 'error'
 ? '✕'
 : '✓'}
 </div>
 <h3
 style={{
 margin: 0,
 fontSize: '18px',
 fontWeight: 700,
 color: 'var(--text-primary, #f3f4f6)',
 }}
 >
 {dialog.options.title}
 </h3>
 </div>

 {/* Message Body */}
 <p
 style={{
 margin: 0,
 fontSize: '14px',
 color: 'var(--text-secondary, #9ca3af)',
 lineHeight: 1.5,
 }}
 >
 {dialog.options.message}
 </p>

 {/* Form Actions Footer Grid */}
 <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
 {dialog.options.type === 'confirm' && (
 <button
 onClick={() => {
 dialog.resolve(false);
 setDialog(null);
 }}
 style={{
 padding: '8px 16px',
 borderRadius: '8px',
 border: '1px solid var(--color-border, #374151)',
 backgroundColor: 'transparent',
 color: 'var(--text-primary, #f3f4f6)',
 fontWeight: 600,
 fontSize: '13px',
 cursor: 'pointer',
 }}
 >
 {dialog.options.cancelText || 'Cancel'}
 </button>
 )}
 <button
 onClick={() => {
 dialog.resolve(true);
 setDialog(null);
 }}
 style={{
 padding: '8px 18px',
 borderRadius: '8px',
 border: 'none',
 backgroundColor:
 dialog.options.type === 'error'
 ? 'var(--color-danger-500, #ef4444)'
 : dialog.options.type === 'confirm'
 ? 'var(--color-brand-500, #3b82f6)'
 : 'var(--color-success-500, #10b981)',
 color: '#ffffff',
 fontWeight: 600,
 fontSize: '13px',
 cursor: 'pointer',
 boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
 }}
 >
 {dialog.options.confirmText || 'Okay'}
 </button>
 </div>
 </div>
 </div>
 )}
 </NotificationContext.Provider>
 );
}
